import React, { useState } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Text,
    Avatar,
    Card,
    Badge,
    Field,
    Checkbox,
} from '@fluentui/react-components';
import {
    Person24Regular,
    Shield24Regular,
    Warning24Regular,
    VideoClip24Regular,
    Dismiss24Regular,
} from '@fluentui/react-icons';

interface PatientInfo {
    id: string;
    name: string;
    mrn: string;
    dateOfBirth: string;
    photo?: string;
    age: number;
    gender: string;
    language: string;
    interpreterRequired: boolean;
    medicalConditions: string[];
    allergies: string[];
    riskLevel: 'low' | 'medium' | 'high';
    consultationType: string;
    priority: 'urgent' | 'standard' | 'routine';
}

interface PatientVerificationProps {
    isOpen: boolean;
    patientInfo: PatientInfo;
    deviceLocation: string;
    onConfirm: () => void;
    onCancel: () => void;
    loading?: boolean;
}

export const PatientVerification: React.FC<PatientVerificationProps> = ({
    isOpen,
    patientInfo,
    deviceLocation,
    onConfirm,
    onCancel,
    loading = false,
}) => {
    const [identityConfirmed, setIdentityConfirmed] = useState(false);
    const [patientPresent, setPatientPresent] = useState(false);

    const handleConfirm = () => {
        if (identityConfirmed && patientPresent) {
            onConfirm();
        }
    };

    const canProceed = identityConfirmed && patientPresent;

    const getRiskBadge = (riskLevel: string) => {
        switch (riskLevel) {
            case 'high':
                return (
                    <Badge appearance="filled" color="danger">
                        High Risk
                    </Badge>
                );
            case 'medium':
                return (
                    <Badge appearance="tint" color="warning">
                        Medium Risk
                    </Badge>
                );
            case 'low':
                return (
                    <Badge appearance="outline" color="success">
                        Low Risk
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const getPriorityBadge = (priority: string) => {
        switch (priority) {
            case 'urgent':
                return (
                    <Badge appearance="filled" color="danger">
                        Urgent
                    </Badge>
                );
            case 'standard':
                return (
                    <Badge appearance="tint" color="brand">
                        Standard
                    </Badge>
                );
            case 'routine':
                return (
                    <Badge appearance="outline" color="success">
                        Routine
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    return (
        <Dialog open={isOpen}>
            <DialogSurface style={{ maxWidth: '600px', width: '90vw' }}>
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="close"
                                icon={<Dismiss24Regular />}
                                onClick={onCancel}
                            />
                        }
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Shield24Regular style={{ fontSize: '24px', color: '#0078d4' }} />
                            Patient Identity Verification
                        </div>
                    </DialogTitle>

                    <DialogContent>
                        <Text size={400} style={{ marginBottom: '20px', color: '#605e5c' }}>
                            Please verify patient identity before starting the consultation.
                        </Text>

                        {/* Patient Photo and Basic Info */}
                        <Card style={{ padding: '20px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                <Avatar
                                    image={
                                        patientInfo.photo ? { src: patientInfo.photo } : undefined
                                    }
                                    name={patientInfo.name}
                                    size={120}
                                    style={{
                                        border: '3px solid #0078d4',
                                        borderRadius: '8px',
                                    }}
                                />

                                <div style={{ flex: 1 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '12px',
                                        }}
                                    >
                                        <Text size={600} weight="semibold">
                                            {patientInfo.name}
                                        </Text>
                                        {getPriorityBadge(patientInfo.priority)}
                                        {getRiskBadge(patientInfo.riskLevel)}
                                    </div>

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '8px',
                                        }}
                                    >
                                        <Text size={300} style={{ color: '#605e5c' }}>
                                            <strong>MRN:</strong> {patientInfo.mrn}
                                        </Text>
                                        <Text size={300} style={{ color: '#605e5c' }}>
                                            <strong>DOB:</strong> {patientInfo.dateOfBirth}
                                        </Text>
                                        <Text size={300} style={{ color: '#605e5c' }}>
                                            <strong>Age:</strong> {patientInfo.age}
                                        </Text>
                                        <Text size={300} style={{ color: '#605e5c' }}>
                                            <strong>Gender:</strong> {patientInfo.gender}
                                        </Text>
                                        <Text size={300} style={{ color: '#605e5c' }}>
                                            <strong>Language:</strong> {patientInfo.language}
                                            {patientInfo.interpreterRequired && (
                                                <Badge
                                                    size="small"
                                                    color="warning"
                                                    style={{ marginLeft: '8px' }}
                                                >
                                                    Interpreter
                                                </Badge>
                                            )}
                                        </Text>
                                        <Text size={300} style={{ color: '#605e5c' }}>
                                            <strong>Location:</strong> {deviceLocation}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Medical Information */}
                        <Card style={{ padding: '16px', marginBottom: '20px' }}>
                            <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                                Medical Information
                            </Text>

                            <div style={{ marginBottom: '12px' }}>
                                <Text size={400} weight="medium">
                                    Consultation Type:
                                </Text>
                                <Text size={400} style={{ marginLeft: '8px' }}>
                                    {patientInfo.consultationType}
                                </Text>
                            </div>

                            {patientInfo.medicalConditions.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <Text size={400} weight="medium">
                                        Medical Conditions:
                                    </Text>
                                    <div style={{ marginTop: '4px' }}>
                                        {patientInfo.medicalConditions.map((condition, index) => (
                                            <Badge
                                                key={index}
                                                appearance="outline"
                                                style={{ marginRight: '8px', marginTop: '4px' }}
                                            >
                                                {condition}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {patientInfo.allergies.length > 0 && (
                                <div>
                                    <Text size={400} weight="medium">
                                        <Warning24Regular
                                            style={{
                                                fontSize: '16px',
                                                color: '#d13438',
                                                marginRight: '4px',
                                            }}
                                        />
                                        Allergies:
                                    </Text>
                                    <div style={{ marginTop: '4px' }}>
                                        {patientInfo.allergies.map((allergy, index) => (
                                            <Badge
                                                key={index}
                                                appearance="filled"
                                                color="danger"
                                                style={{ marginRight: '8px', marginTop: '4px' }}
                                            >
                                                {allergy}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Verification Checkboxes */}
                        <Card style={{ padding: '16px', backgroundColor: '#f8f9fa' }}>
                            <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                                Pre-Call Verification
                            </Text>

                            <Field>
                                <Checkbox
                                    checked={identityConfirmed}
                                    onChange={(_, data) =>
                                        setIdentityConfirmed(data.checked === true)
                                    }
                                    label="I have verified the patient's identity matches the information displayed above"
                                />
                            </Field>

                            <Field style={{ marginTop: '12px' }}>
                                <Checkbox
                                    checked={patientPresent}
                                    onChange={(_, data) => setPatientPresent(data.checked === true)}
                                    label="I confirm the patient is present and ready for the consultation"
                                />
                            </Field>
                        </Card>
                    </DialogContent>

                    <DialogActions>
                        <Button appearance="secondary" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            icon={<VideoClip24Regular />}
                            onClick={handleConfirm}
                            disabled={!canProceed || loading}
                        >
                            {loading ? 'Starting Call...' : 'Proceed to Consent'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
